import { useState, useRef, useContext, ChangeEvent } from 'react'
import {
  Box,
  Button,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material'
import Header from 'components/Header'
import { SnackbarSeverity, uploadOperations } from 'constant'
import { useParams } from 'react-router-dom'
import { markOrdersAsSpecialByFile, uploadOrder } from 'api/daily-order'
import { SnackContext } from 'contexts/SnackContextProvider'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

const AddOrUpdateDailyOrder = () => {
  const { company } = useParams()
  const [operation, setOperation] = useState(uploadOperations[0].value)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const { handleOpenSnackbar } = useContext(SnackContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handleChangeOperation = (event: any) => {
    setOperation(event.target.value)
  }
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError(false)
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0])
    }
  }
  const handleRemoveFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  const handleSubmitUploadOrder = async () => {
    if (!selectedFile) {
      setError(true)
      console.warn('No file selected.')
      return
    }
    setLoading(true)
    let result: any
    try {
      const isAddOperation = [
        'single-item-order',
        'multiple-items-order',
        'box-code-order'
      ].includes(operation)

      if (isAddOperation) {
        result = await uploadOrder(`${company}`, operation, selectedFile)
      } else {
        result = await markOrdersAsSpecialByFile(`${company}`, selectedFile)
      }

      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          isAddOperation
            ? 'File uploaded successfully. Orders added.'
            : 'File uploaded successfully. Orders updated as special.'
        )
      } else {
        handleOpenSnackbar(SnackbarSeverity.Error, result.data.message)
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMessage ||
        'An error occurred while uploading the file'
      handleOpenSnackbar(SnackbarSeverity.Error, errorMessage)
      console.error('Upload failed:', error)
    } finally {
      setError(false)
      setLoading(false)
    }
  }

  const handleDownloadTemplate = (templateName: string) => {
    const link = document.createElement('a')
    link.href = `/templates/${templateName}`
    link.download = templateName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const templates = [
    {
      name: 'single item order template.xlsx',
      title: 'Single Item Order Template',
      description: 'For uploading orders with single items'
    },
    {
      name: 'multiple item order template.xlsx',
      title: 'Multiple Item Order Template',
      description: 'For uploading orders with multiple items'
    },
    {
      name: 'special order template.xlsx',
      title: 'Special Order Template',
      description: 'For marking orders as special orders'
    }
  ]

  return (
    <Box>
      <Header />
      <Box
        sx={{
          width: '80%',
          margin: '50px auto',
          '& button': {
            display: 'block',
            margin: '30px auto',
            padding: '10px 40px'
          },
          '& .upload-button': {
            display: 'block',
            textAlign: 'center',
            marginTop: '20px'
          }
        }}
      >
        <Box sx={{ marginBottom: '40px' }}>
          <Typography
            variant='h5'
            sx={{ marginBottom: '20px', fontWeight: 'bold', color: '#364c63' }}
          >
            Download Template Files
          </Typography>
          <Typography
            variant='body2'
            sx={{ marginBottom: '20px', color: '#666' }}
          >
            Please download the appropriate template file first, fill it out and
            then upload
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
              justifyContent: 'center'
            }}
          >
            {templates.map((template, index) => (
              <Card key={index} sx={{ minWidth: 250, maxWidth: 300 }}>
                <CardContent>
                  <Typography
                    variant='h6'
                    component='div'
                    sx={{ fontWeight: 'bold', marginBottom: '10px' }}
                  >
                    {template.title}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {template.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => handleDownloadTemplate(template.name)}
                    sx={{
                      backgroundColor: '#364c63',
                      '&:hover': {
                        backgroundColor: '#4b6485'
                      }
                    }}
                  >
                    Download
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>

        <Divider sx={{ margin: '40px 0' }} />

        <Typography
          variant='h5'
          sx={{ marginBottom: '20px', fontWeight: 'bold', color: '#364c63' }}
        >
          Upload Order Files
        </Typography>
        <InputLabel sx={{ color: '#000000' }}>Choose your operation</InputLabel>
        <Select onChange={handleChangeOperation} fullWidth value={operation}>
          {uploadOperations.map(item => (
            <MenuItem key={item.label} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
        <Button className='upload-button' variant='outlined' component='label'>
          Upload files
          <VisuallyHiddenInput
            type='file'
            onChange={handleFileChange}
            ref={fileInputRef}
          />
        </Button>
        {selectedFile && (
          <Box sx={{ width: '100%' }}>
            <Typography variant='subtitle1'>File to Upload:</Typography>
            <Typography>
              {selectedFile.name} - {(selectedFile.size / 1024).toFixed(2)} KB
            </Typography>
            <Typography
              onClick={handleRemoveFile}
              sx={{ marginTop: '10px', color: 'red', cursor: 'pointer' }}
            >
              Remove File
            </Typography>
          </Box>
        )}
        {error && (
          <Typography
            variant='subtitle1'
            sx={{ color: 'red', textAlign: 'center' }}
          >
            Please select a file to upload.
          </Typography>
        )}
        <Button
          variant='contained'
          onClick={() => {
            handleSubmitUploadOrder()
          }}
          loading={loading}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default AddOrUpdateDailyOrder
